import { useQuery } from "@tanstack/react-query"
import BaseGrowBody from "../../../components/elements/BaseGrowBody"
import BaseHeadBanner from "../../../components/elements/BaseHeadBanner"
import BasePage from "../../../components/layout/BasePage"
import { GeneralQueryKeys } from "../../../api/constants"
import BaseSection from "../../../components/elements/BaseSection"
import { getSessionsGroupedByEmployee } from "../api/apiCalls"
import dayjs from "dayjs"
import BaseButton from "../../../components/elements/BaseButton"
import isoWeek from "dayjs/plugin/isoWeek"
import { useState } from "react"
import Loading from "../../../components/common/Loading"
import ErrorPage from "../../404/ErrorPage"

dayjs.extend(isoWeek)

export default function Overview() {
    const [currentWeek, setCurrentWeek] = useState(dayjs().isoWeek())
    const [startAt, setStartAt] = useState(dayjs().startOf("isoWeek"))
    const queryKey = [GeneralQueryKeys.EMPLOYEE_NAMES_ONLY, startAt.toDate(), startAt.add(6, "day").toDate()]
    const {
        data,
        error,
        isLoading,
    } = useQuery({
        queryKey: [queryKey],
        queryFn: () => getSessionsGroupedByEmployee(startAt.toDate(), startAt.add(6, "day").toDate()),
    })
    return (
        <BasePage>
            <BaseHeadBanner
                title="Übersicht"
                button={
                    <div className="flex flex-row gap-2 items-center">
                        <p className="text-sm text-gray-300 pr-4">Kalenderwoche</p>
                        <BaseButton
                            classProps={Number(currentWeek) === dayjs().isoWeek() ? "text-xl" : ""}
                            label={dayjs().isoWeek().toString()}
                            func={() => {
                                setCurrentWeek(dayjs().isoWeek())
                                setStartAt(dayjs().startOf("isoWeek"))
                            }}
                            icon={undefined}
                        />
                        <BaseButton
                            classProps={Number(currentWeek) === dayjs().isoWeek() + 1 ? "text-xl" : ""}
                            label={(dayjs().isoWeek() + 1).toString()}
                            func={() => {
                                setCurrentWeek(dayjs().isoWeek() + 1)
                                setStartAt(dayjs().startOf("isoWeek").add(7, "day"))
                            }}
                            icon={undefined}
                        />
                        <BaseButton
                            classProps={Number(currentWeek) === dayjs().isoWeek() + 2 ? "text-xl" : ""}
                            label={(dayjs().isoWeek() + 2).toString()}
                            func={() => {
                                setCurrentWeek(dayjs().isoWeek() + 2)
                                setStartAt(dayjs().startOf("isoWeek").add(14, "day"))
                            }}
                            icon={undefined}
                        />
                    </div>
                }
            />
            { isLoading && <Loading /> }
            { error && <ErrorPage /> }
            { !isLoading && !error && data && data.length === 0 && <BaseSection classProps="mt-4 flex !w-full justify-center items-center">
                <p className="text-sm text-gray-300">Keine Daten in dieser Woche gefunden</p>
            </BaseSection> }
            { !isLoading && !error && data && data.length > 0 && <BaseSection classProps="mt-4 !overflow-x-scroll flex !w-full">
                <div className="flex flex-row gap-4 w-fit">
                    {data?.map((item: any) => (
                        <OverviewName key={item.employee._id} employee={item.employee} />
                    ))}
                </div>
                <BaseGrowBody classProps="flex flex-row gap-4 overflow-y-auto !w-fit">
                    {data?.map((item: any) => (
                        <OverviewElement key={item.employee._id} sessions={item.sessions} startAt={startAt.toDate()} />
                    ))}
                </BaseGrowBody>
            </BaseSection> }
        </BasePage>
    )
}

type OverviewElementType = {
    sessions: any
    startAt: Date
}

function OverviewElement(props: OverviewElementType) {
    console.log(props.startAt)
    
    // Helper function to get sessions for a specific day
    const getSessionsForDay = (dayOffset: number) => {
        return props.sessions.filter((session: any) =>
            dayjs(session.startAt).isSame(dayjs(props.startAt).add(dayOffset, "day"), "day")
        )
    }
    
    return (
        <div className="flex flex-col gap-4 !w-80 border-r h-fit pr-4">
            <div className="flex flex-col min-h-[24rem] border-b text-sm overflow-y-auto">
                <h3>Montag | {dayjs(props.startAt).format("DD.MM.YYYY")}</h3>
                {getSessionsForDay(0).length > 0 ? (
                    getSessionsForDay(0).map((session: any) => (
                        <div key={session._id} className="bg-primary/50 border-primary border-l-4 rounded-default p-1 text-xs mt-1">
                            <p className="truncate">{session.title}</p>
                            <p>{dayjs(session.startAt).format("HH:mm")} Uhr - {dayjs(session.endAt).format("HH:mm")} Uhr</p>
                        </div>
                    ))
                ) : (
                    <p className="text-xs text-gray-500 mt-1">Keine Termine an diesem Tag</p>
                )}
            </div>
            <div className="flex flex-col min-h-[24rem] border-b text-sm">
                <h3>Dienstag | {dayjs(props.startAt).add(1, "day").format("DD.MM.YYYY")}</h3>
                {getSessionsForDay(1).length > 0 ? (
                    getSessionsForDay(1).map((session: any) => (
                        <div key={session._id} className="rounded-md bg-primary/50 border-primary border-2 p-1 text-xs mt-1">
                            <p className="truncate">{session.title}</p>
                            <p>{dayjs(session.startAt).format("HH:mm")} Uhr - {dayjs(session.endAt).format("HH:mm")} Uhr</p>
                        </div>
                    ))
                ) : (
                    <p className="text-xs text-gray-500 mt-1">Keine Termine an diesem Tag</p>
                )}
            </div>
            <div className="flex flex-col min-h-[24rem] border-b text-sm">
                <h3>Mittwoch | {dayjs(props.startAt).add(2, "day").format("DD.MM.YYYY")}</h3>
                {getSessionsForDay(2).length > 0 ? (
                    getSessionsForDay(2).map((session: any) => (
                        <div key={session._id} className="rounded-md bg-primary/50 border-primary border-2 p-1 text-xs mt-1">
                            <p className="truncate">{session.title}</p>
                            <p>{dayjs(session.startAt).format("HH:mm")} Uhr - {dayjs(session.endAt).format("HH:mm")} Uhr</p>
                        </div>
                    ))
                ) : (
                    <p className="text-xs text-gray-500 mt-1">Keine Termine an diesem Tag</p>
                )}
            </div>
            <div className="flex flex-col min-h-[24rem] border-b text-sm">
                <h3>Donnerstag | {dayjs(props.startAt).add(3, "day").format("DD.MM.YYYY")}</h3>
                {getSessionsForDay(3).length > 0 ? (
                    getSessionsForDay(3).map((session: any) => (
                        <div key={session._id} className="rounded-md bg-primary/50 border-primary border-2 p-1 text-xs mt-1">
                            <p className="truncate">{session.title}</p>
                            <p>{dayjs(session.startAt).format("HH:mm")} Uhr - {dayjs(session.endAt).format("HH:mm")} Uhr</p>
                        </div>
                    ))
                ) : (
                    <p className="text-xs text-gray-500 mt-1">Keine Termine an diesem Tag</p>
                )}
            </div>
            <div className="flex flex-col min-h-[24rem] border-b text-sm">
                <h3>Freitag | {dayjs(props.startAt).add(4, "day").format("DD.MM.YYYY")}</h3>
                {getSessionsForDay(4).length > 0 ? (
                    getSessionsForDay(4).map((session: any) => (
                        <div key={session._id} className="rounded-md bg-primary/50 border-primary border-2 p-1 text-xs mt-1">
                            <p className="truncate">{session.title}</p>
                            <p>{dayjs(session.startAt).format("HH:mm")} Uhr - {dayjs(session.endAt).format("HH:mm")} Uhr</p>
                        </div>
                    ))
                ) : (
                    <p className="text-xs text-gray-500 mt-1">Keine Termine an diesem Tag</p>
                )}
            </div>
        </div>
    )
}

type OverviewNameType = {
    employee: {
        first_name: string
        last_name: string
    }
}

function OverviewName(props: OverviewNameType) {
    return (
        <p className="border-b-2 border-black pb-2 font-semibold uppercase truncate !w-80">
            {props.employee.first_name} {props.employee.last_name}
        </p>
    )
}
